<template>
  <div class="space-y-3">
    <div class="flex flex-wrap gap-2">
      <button
        v-for="origin in leadOrigins"
        :key="origin.value"
        type="button"
        @click="selectOrigin(origin.value)"
        class="inline-flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all duration-200 hover:shadow-md shrink-0"
        :class="[
          selectedOrigin === origin.value
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
            : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500'
        ]"
      >
        <div class="lead-origin-icon w-8 h-8 rounded-lg flex items-center justify-center shrink-0">
          <LeadSourceIcons
            v-if="origin.iconComponent"
            :name="origin.iconComponent"
          />
          <svg
            v-else
            class="w-5 h-5"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fill-rule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z"
              clip-rule="evenodd"
            />
          </svg>
        </div>
        <span class="text-sm font-medium whitespace-nowrap">{{ origin.label }}</span>
        <div v-if="selectedOrigin === origin.value" class="flex-shrink-0">
          <svg class="w-3 h-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>
        </div>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import LeadSourceIcons from './icons/LeadSourceIcons.vue'
import { getLeadSourceIcon } from '@/utils/leadSourceIconMapper'

const { t } = useI18n()

const props = defineProps({
  modelValue: String,
  origins: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['update:modelValue'])

const selectedOrigin = ref(props.modelValue || '')

const DEFAULT_ORIGINS = [
  { origem: 'WhatsApp', icone: 'whatsapp' },
  { origem: 'Site', icone: 'website' },
  { origem: 'Indicação', icone: 'referral' },
  { origem: 'Facebook', icone: 'facebook' },
  { origem: 'Instagram', icone: 'instagram' },
  { origem: 'Google', icone: 'google' },
  { origem: 'Outros', icone: 'other' }
]

const ORIGIN_I18N_KEYS = {
  WhatsApp: 'whatsapp',
  Site: 'website',
  Indicação: 'referral',
  Facebook: 'facebook',
  Instagram: 'instagram',
  Google: 'google',
  Outros: 'other'
}

const originLabel = (origem) => {
  const key = ORIGIN_I18N_KEYS[origem]
  return key ? t(`kanban.leadOrigin.${key}`) : origem
}

const mapOrigin = (origin) => ({
  value: origin.origem,
  label: originLabel(origin.origem),
  iconKey: origin.icone || '',
  iconComponent: origin.icone ? getLeadSourceIcon(origin.icone) : null
})

const leadOrigins = computed(() => {
  const list = props.origins?.length ? props.origins : DEFAULT_ORIGINS
  return list.map(mapOrigin)
})

const selectOrigin = (value) => {
  selectedOrigin.value = value
  emit('update:modelValue', value)
}

watch(() => props.modelValue, (newValue) => {
  selectedOrigin.value = newValue || ''
})
</script>

<style scoped>
.lead-origin-icon :deep(svg) {
  width: 1.25rem;
  height: 1.25rem;
}

:global(body.tema_clara) .lead-origin-icon :deep(svg) {
  color: #1f2937;
}

:global(body.tema_escura) .lead-origin-icon :deep(svg) {
  color: #f9fafb;
}

:global(body:not(.tema_clara):not(.tema_escura)) .lead-origin-icon :deep(svg) {
  color: #374151;
}

:global(body:not(.tema_clara):not(.tema_escura).dark) .lead-origin-icon :deep(svg),
:global(html.dark) .lead-origin-icon :deep(svg) {
  color: #f9fafb;
}
</style>
